// Picks the Softline supplier a document came from out of masterdata hits —
// pure, so the identity rules are testable without HTTP.
import type { MasterdataSupplier } from '@softapps/core/masterdata';
import { foldName, sameSupplier } from './product-match';

export interface DocumentSupplier {
  name?: string | null;
  uid?: string | null;
}

const uidKey = (value: string | null | undefined): string =>
  (value ?? '').replace(/[^a-z0-9]/gi, '').toUpperCase();

export function supplierDisplayName(supplier: MasterdataSupplier): string {
  return [supplier.name1, supplier.name2, supplier.name3]
    .filter(Boolean)
    .join(' ')
    .trim();
}

/** Lieferantennummer with its subnumber (`123` or `123/4`), as masterdata shows it. */
export function supplierNumberLabel(supplier: MasterdataSupplier): string {
  return supplier.supplierSubnumber > 0
    ? `${supplier.supplierNumber}/${supplier.supplierSubnumber}`
    : String(supplier.supplierNumber ?? '');
}

/**
 * The UID identifies a company; a name only counts when exactly one candidate
 * carries it (legal-form suffixes compared by containment, see product-match).
 */
export function pickSupplier(
  candidates: readonly MasterdataSupplier[],
  document: DocumentSupplier
): MasterdataSupplier | null {
  const uid = uidKey(document.uid);
  if (uid) {
    const byUid = candidates.filter((s) => uidKey(s.uid) === uid);
    if (byUid.length === 1) return byUid[0];
  }
  const name = foldName(document.name);
  if (!name) return null;
  const byName = candidates.filter((s) =>
    sameSupplier(foldName(supplierDisplayName(s)), name)
  );
  return byName.length === 1 ? byName[0] : null;
}
